import type { AdminPolicyDetail, PolicyVersionDetail } from '@inflip/contracts/admin';
import {
  POLICY_DEFAULT_LANGUAGE,
  POLICY_LANGUAGE_LABELS,
  POLICY_LANGUAGES,
  POLICY_VERSION_STATUS_LABELS,
  type PolicyLanguage,
  startOfZonedDate,
  zonedDateOf,
} from '@inflip/shared';
import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { BackLink } from '../components/back-link';
import { PolicyEditor } from '../components/policy-editor/policy-editor';
import { Select } from '../components/select';
import { Button, ConfirmDialog, Dialog, ErrorBanner, inputCls } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import {
  POLICY_KIND_OPTIONS,
  policyKindFields,
  policyKindOf,
  type PolicyKind,
} from '../lib/policy-kind';
import { adminTimeZone, formatDate } from '../lib/time-zone';

const STATUS_TEXT: Record<string, string> = {
  DRAFT: 'text-amber-600',
  PUBLISHED: 'text-emerald-600',
  ARCHIVED: 'text-muted-foreground',
};

type Draft = { title: string; content: string };
type Drafts = Record<PolicyLanguage, Draft>;

const EMPTY_DRAFTS = (): Drafts =>
  Object.fromEntries(POLICY_LANGUAGES.map((l) => [l, { title: '', content: '' }])) as Drafts;

function draftsOf(detail: PolicyVersionDetail): Drafts {
  const drafts = EMPTY_DRAFTS();
  for (const c of detail.contents) drafts[c.language] = { title: c.title, content: c.content };
  return drafts;
}

/** 본문에 글자가 있는지 — 빈 에디터는 <p></p> 만 남는다 (서버 isBlankPolicyHtml 과 같은 뜻) */
function hasText(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim() !== ''
  );
}

/** 탭의 ● — 제목·본문이 다 있으면 채운 언어 */
function isFilled(d: Draft): boolean {
  return d.title.trim() !== '' && hasText(d.content);
}

/**
 * 약관 버전 편집기 — 좌: 버전 목록 / 우: 언어 탭(한국어·English·日本語) + 제목 + 리치 에디터.
 * 한국어 제목·본문이 있어야 게시하고, 번역이 빈 언어는 회원에게 한국어로 대신 보인다.
 * 발행본은 발행 +7일 이후 동결. 초안·보관본은 수정 가능. 롤백 = 보관된 버전을 다시 발행.
 */
export function PolicyDetailPage() {
  const { policyId } = useParams({ from: '/policies/$policyId' });
  const { session } = useAdminAuth();
  const [policy, setPolicy] = useState<AdminPolicyDetail | null>(null);
  const [linkTargets, setLinkTargets] = useState<{ slug: string; title: string }[]>([]);
  const [selected, setSelected] = useState<PolicyVersionDetail | null>(null);
  const [drafts, setDrafts] = useState<Drafts>(EMPTY_DRAFTS);
  const [saved, setSaved] = useState<Drafts>(EMPTY_DRAFTS);
  const [language, setLanguage] = useState<PolicyLanguage>(POLICY_DEFAULT_LANGUAGE);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | 'publish' | 'delete' | 'rollback'>(null);

  const token = session?.accessToken;

  const showVersion = (detail: PolicyVersionDetail) => {
    setSelected(detail);
    const next = draftsOf(detail);
    setDrafts(next);
    setSaved(next);
  };

  const load = useCallback(
    (selectVersionId?: string) => {
      if (!token) return Promise.resolve();
      return api
        .getPolicy(token, policyId)
        .then(async (p) => {
          setPolicy(p);
          const target =
            p.versions.find((v) => v.id === selectVersionId) ??
            p.versions.find((v) => v.status === 'DRAFT') ??
            p.versions[0];
          if (target) showVersion(await api.getPolicyVersion(token, policyId, target.id));
        })
        .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    },
    [token, policyId],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // 본문의 '약관 링크'로 고를 다른 약관
  useEffect(() => {
    if (!token) return;
    api
      .listPolicies(token)
      .then((rows) =>
        setLinkTargets(
          rows.filter((p) => p.id !== policyId).map((p) => ({ slug: p.slug, title: p.title })),
        ),
      )
      .catch(() => undefined);
  }, [token, policyId]);

  const dirtyLanguages = useMemo(
    () =>
      POLICY_LANGUAGES.filter(
        (l) => drafts[l].title !== saved[l].title || drafts[l].content !== saved[l].content,
      ),
    [drafts, saved],
  );
  const dirty = dirtyLanguages.length > 0;

  async function selectVersion(versionId: string) {
    if (!token) return;
    showVersion(await api.getPolicyVersion(token, policyId, versionId));
  }

  async function run(fn: () => Promise<unknown>, reselect?: string) {
    if (!token || pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      await load(reselect);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  /** 바뀐 언어만 저장 — 아직 아무것도 안 적은 번역 탭은 건너뛴다 */
  async function saveDirty(versionId: string) {
    if (!token) return;
    for (const l of dirtyLanguages) {
      const d = drafts[l];
      if (!d.title.trim() && !hasText(d.content)) continue;
      if (!d.title.trim()) {
        setLanguage(l);
        throw new Error(`${POLICY_LANGUAGE_LABELS[l]} 제목을 적어 주세요.`);
      }
      await api.savePolicyContent(token, policyId, versionId, l, d);
    }
  }

  if (!policy || !session) return null;
  const isDraft = selected?.status === 'DRAFT';
  const isArchived = selected?.status === 'ARCHIVED';
  // 발행본 오타 수정 창 (발행 +7일)
  const inEditWindow =
    selected?.status === 'PUBLISHED' &&
    !!selected.editableUntil &&
    new Date(selected.editableUntil).getTime() > Date.now();
  const isEditable = isDraft || isArchived || inEditWindow;
  const koreanReady = isFilled(drafts[POLICY_DEFAULT_LANGUAGE]);

  // 보관본 발행 경로: 발행본보다 새 버전(과거 롤백으로 밀려난 것)은 "발행",
  // 발행본 바로 이전 버전만 "롤백 (재발행)". 그보다 오래된 버전은 발행 불가.
  const publishedVersion = policy.versions.find((v) => v.status === 'PUBLISHED')?.version ?? null;
  const isForwardPublish =
    isArchived && (publishedVersion === null || selected!.version > publishedVersion);
  const rollbackTarget =
    publishedVersion === null
      ? undefined
      : policy.versions.find((v) => v.version < publishedVersion); // DESC 정렬 — 첫 매치가 바로 이전
  const isRollbackTarget = isArchived && selected?.id === rollbackTarget?.id;

  const save = () => selected && void run(() => saveDirty(selected.id), selected.id);
  const openPublish = (kind: 'publish' | 'rollback') => {
    if (!koreanReady) {
      setLanguage(POLICY_DEFAULT_LANGUAGE);
      setError('한국어 제목과 본문을 채워야 게시할 수 있어요.');
      return;
    }
    setDialog(kind);
  };
  const draft = drafts[language];
  const setDraft = (patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [language]: { ...prev[language], ...patch } }));

  return (
    <main className="w-full px-6 py-8">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <BackLink
          fallback="/policies"
          fallbackLabel="약관 관리"
          className="text-sm text-muted-foreground hover:underline"
        />
        <h1 className="text-xl font-extrabold tracking-tight">{policy.title}</h1>
        <span className="font-mono text-xs text-muted-foreground">/{policy.slug}</span>
        <Select
          label="동의 방식"
          appearance="filled"
          className="ml-auto w-48"
          value={policyKindOf(policy)}
          options={POLICY_KIND_OPTIONS}
          disabled={pending}
          onValueChange={(v) =>
            void run(() =>
              api.updatePolicy(session.accessToken, policyId, policyKindFields(v as PolicyKind)),
            )
          }
        />
      </div>
      {error && <ErrorBanner message={error} />}

      <div className="flex gap-6">
        {/* 버전 목록 */}
        <aside className="w-56 shrink-0">
          <Button
            className="mb-3 w-full"
            disabled={pending}
            onClick={() => void run(() => api.createPolicyVersion(session.accessToken, policyId))}
          >
            + 새 버전 (초안)
          </Button>
          <div className="space-y-1.5">
            {policy.versions.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => void selectVersion(v.id)}
                className={`w-full rounded-md p-2.5 text-left text-sm transition ${
                  selected?.id === v.id ? 'bg-brand-soft' : 'hover:bg-muted'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="font-mono font-bold">v{v.version}</span>
                  <span className={`text-xs font-bold ${STATUS_TEXT[v.status]}`}>
                    {POLICY_VERSION_STATUS_LABELS[v.status]}
                  </span>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    {v.effectiveAt && v.effectiveAt !== v.publishedAt
                      ? `시행 ${formatDate(v.effectiveAt)}`
                      : formatDate(v.publishedAt ?? v.updatedAt)}
                  </span>
                </span>
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {v.languages.length > 0
                    ? v.languages.map((l) => POLICY_LANGUAGE_LABELS[l]).join(' · ')
                    : '본문 없음'}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* 편집기 */}
        {selected && (
          <section className="min-w-0 flex-1">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className={`text-sm font-bold ${STATUS_TEXT[selected.status]}`}>
                v{selected.version} · {POLICY_VERSION_STATUS_LABELS[selected.status]}
              </span>
              {inEditWindow && (
                <span className="text-xs font-semibold text-amber-700">
                  발행 후 7일 이내 — 오타 수정 가능 ({formatDate(selected.editableUntil!)} 까지)
                </span>
              )}
              {!isEditable && (
                <span className="text-xs text-muted-foreground">
                  발행 후 7일이 지나 수정할 수 없어요. 개정은 새 버전으로 하세요.
                </span>
              )}
              <div className="ml-auto flex gap-2">
                {isDraft && (
                  <>
                    <Button variant="outline" disabled={pending || !dirty} onClick={save}>
                      임시저장
                    </Button>
                    <Button disabled={pending} onClick={() => openPublish('publish')}>
                      발행
                    </Button>
                    <Button variant="danger" disabled={pending} onClick={() => setDialog('delete')}>
                      삭제
                    </Button>
                  </>
                )}
                {inEditWindow && (
                  <Button disabled={pending || !dirty} onClick={save}>
                    오타 수정 저장
                  </Button>
                )}
                {isArchived && (
                  <>
                    <Button variant="outline" disabled={pending || !dirty} onClick={save}>
                      저장
                    </Button>
                    {isForwardPublish && (
                      <Button disabled={pending} onClick={() => openPublish('publish')}>
                        발행
                      </Button>
                    )}
                    {isRollbackTarget && (
                      <Button disabled={pending} onClick={() => openPublish('rollback')}>
                        이 버전으로 롤백 (재발행)
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* 언어 탭 — ● 는 제목·본문을 채운 언어, 고친 채 저장 안 한 언어는 * */}
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              {POLICY_LANGUAGES.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => setLanguage(l)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                    language === l ? 'bg-foreground text-background' : 'bg-muted hover:bg-border/60'
                  }`}
                >
                  {POLICY_LANGUAGE_LABELS[l]}
                  {isFilled(drafts[l]) && <span className="ml-1">●</span>}
                  {dirtyLanguages.includes(l) && <span className="ml-0.5">*</span>}
                </button>
              ))}
              <span className="ml-2 text-xs text-muted-foreground">
                {language === POLICY_DEFAULT_LANGUAGE
                  ? '한국어는 게시에 필요해요.'
                  : '비워 두면 회원에게 한국어로 대신 보여요.'}
              </span>
            </div>

            <input
              className={`mb-3 w-full font-semibold disabled:bg-muted ${inputCls}`}
              value={draft.title}
              disabled={!isEditable}
              onChange={(e) => setDraft({ title: e.target.value })}
              placeholder={`문서 제목 (${POLICY_LANGUAGE_LABELS[language]}, 회원에게 표시)`}
            />
            <PolicyEditor
              key={`${selected.id}:${language}`}
              value={draft.content}
              onChange={(content) => setDraft({ content })}
              editable={isEditable}
              policies={linkTargets}
            />
          </section>
        )}
      </div>

      {(dialog === 'publish' || dialog === 'rollback') && selected && (
        <PublishDialog
          title={
            dialog === 'publish'
              ? `v${selected.version} 을 발행할까요?`
              : `v${selected.version} 을 다시 발행할까요?`
          }
          description={`${
            dialog === 'publish'
              ? '기존 발행본은 보관되고, 사용자에게는 이 버전이 보여요.'
              : '보관된 버전으로 롤백해요. 현재 발행본은 보관돼요.'
          } 번역: ${POLICY_LANGUAGES.filter((l) => isFilled(drafts[l]))
            .map((l) => POLICY_LANGUAGE_LABELS[l])
            .join(' · ')} (빈 언어는 한국어로 보여요)`}
          confirmLabel={dialog === 'publish' ? '발행' : '재발행'}
          required={policy.isRequired && policy.isActive}
          busy={pending}
          onConfirm={(effectiveAt) =>
            void run(async () => {
              if (dirty) await saveDirty(selected.id);
              await api.publishPolicyVersion(session.accessToken, policyId, selected.id, {
                effectiveAt,
              });
            }, selected.id)
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'delete' && selected && (
        <ConfirmDialog
          title="이 초안을 삭제할까요?"
          description="삭제한 초안은 되돌릴 수 없어요."
          confirmLabel="삭제"
          danger
          busy={pending}
          onConfirm={() =>
            void run(() => api.deletePolicyVersion(session.accessToken, policyId, selected.id))
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}

/**
 * 발행 창 — 시행일을 고른다. 오늘이면 지금부터, 다른 날이면 그날 00:00(어드민 시간대)부터 시행.
 * 필수 약관은 시행일 전에는 회원이 동의를 미룰 수 있고, 시행일부터는 동의해야 계속 쓴다.
 */
function PublishDialog({
  title,
  description,
  confirmLabel,
  required,
  busy,
  onConfirm,
  onClose,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  required: boolean;
  busy: boolean;
  onConfirm(effectiveAt: string | undefined): void;
  onClose(): void;
}) {
  const timeZone = adminTimeZone();
  const today = zonedDateOf(new Date(), timeZone);
  const [date, setDate] = useState(today);
  const immediate = date <= today;
  return (
    <Dialog label={title} onClose={onClose}>
      <p className="text-lg font-bold tracking-tight">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      <label className="mt-4 block text-sm font-semibold">
        시행일
        <input
          type="date"
          className={`mt-1.5 w-full ${inputCls}`}
          min={today}
          value={date}
          onChange={(e) => setDate(e.target.value || today)}
        />
      </label>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {required
          ? immediate
            ? '오늘 바로 시행해요. 이 버전에 동의하지 않은 회원은 다음에 들어올 때 동의해야 계속 이용할 수 있어요.'
            : `${date} 00:00 부터 시행해요. 그 전에는 회원이 동의 창을 닫고 넘어갈 수 있고, 시행일부터는 동의해야 계속 이용할 수 있어요.`
          : '선택 약관이라 동의를 다시 받지 않아요. 시행일은 공개 화면에 표시돼요.'}
      </p>
      <div className="mt-5 flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          className="flex-1"
          disabled={busy}
          onClick={() =>
            onConfirm(immediate ? undefined : startOfZonedDate(date, timeZone).toISOString())
          }
        >
          {confirmLabel}
        </Button>
      </div>
    </Dialog>
  );
}
